<template>
  <button class="custom-button" :class="[buttonClass, { 'disabled': disabled, 'active': isActive }]"
    @click="handleClick" :disabled="disabled" :style="{ width: customWidth }">
    <span class="button-content" :class="[contentClass]">
      {{ label }}
      <img v-if="icon || type === 'dropdown'" :src="type === 'dropdown' ? iconDropdown : icon" :alt="iconAlt" class="button-icon"
        :class="[iconClass, { 'active': (type === 'dropdown' && isActive) }]" @error="handleIconError" />
    </span>
  </button>
</template>

<script lang="ts">
import { defineComponent, computed, ref } from 'vue';

export default defineComponent({
  name: 'AppButton',
  props: {
    label: {
      type: String,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      default: 'primary',
      validator: (value: string) => ['primary', 'secondary', 'link', 'tag', 'dropdown'].includes(value)
    },
    width: {
      type: String,
      default: ''
    },
    icon: {
      type: String,
      default: ''
    },
    iconAlt: {
      type: String,
      default: ''
    },
    active: {
      type: Boolean,
      default: false
    }
  },
  emits: ['click'],
  setup(props, { emit }) {
    const buttonClass = `custom-button__${props.type}`;
    const contentClass = `button-content__${props.type}`;
    const iconClass = `button-icon__${props.type}`;
    const iconError = ref(false);
    const isPressed = ref(false);
    const isActive = computed(() => props.active || isPressed.value);

    const iconDropdown = new URL('@/assets/image/icon-button-dropdown.svg', import.meta.url).href;

    const handleClick = () => {
      if (!props.disabled) {
        emit('click');
      }
    };

    const customWidth = computed(() => {
      if (props.width) {
        return props.width;
      }
      return '';
    });

    const handleIconError = () => {
      iconError.value = true;
      console.error(`Не удалось загрузить иконку: ${props.icon}`);
    };

    return {
      buttonClass,
      contentClass,
      iconClass,
      isActive,
      iconDropdown,
      handleClick,
      customWidth,
      handleIconError,
      iconError
    };
  }
});
</script>

<style lang="scss" scoped>
@import '@/styles/color.scss';

.custom-button {
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  flex-direction: row;
  align-items: center;
  padding: 10px 16px;
  cursor: pointer;

  &__primary {
    height: 32px;
    width: max-content;
    background: $color-accent;
    box-shadow: inset 0px -2px 0px $color-on-accent;
    border-radius: 8px;
    border: none;

    &:hover {
      background: $color-on-accent;
    }
  }

  &__secondary {
    height: 32px;
    width: max-content;
    background: $background-white;
    border-radius: 8px;
    border: none;

    &:hover {
      background: $color-divider;
    }
  }

  &__link {
    padding: 0px;
    height: 18px;
    width: max-content;
    background: transparent;
    border: none;
  }

  &__tag {
    padding: 1px 4px;
    height: 14px;
    width: max-content;
    background: $color-divider;
    border-radius: 8px;
    border: none;

    &:hover {
      background: $background-card;
    }

    &:active,
    &.active {
      background: $color-accent;

      &:hover {
        background: $color-on-accent;
      }
    }
  }

  &__dropdown {
    padding: 10px 12px;
    height: 32px;
    width: max-content;
    background: $background-white;
    border: 1px solid $color-border;
    border-radius: 8px;

    &:hover {
      border: 1px solid $color-on-accent;
    }
  }
}

.button-content {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'Roboto';
  font-size: 14px;
  font-style: normal;
  line-height: 18px;

  &__primary {
    color: $background-white;
    border: none;
    background-color: transparent;
  }

  &__secondary {
    color: $color-text-secondary;
    font-weight: 600;
  }

  &__link {
    color: $color-text-secondary;
    font-weight: 400;
    white-space: nowrap;

    &:hover {
      text-decoration: underline;
    }
  }

  &__tag {
    color: $color-text-secondary;
    font-size: 12px;
    line-height: 14px;
    font-weight: 400;

    &:active {
      color: $background-white !important;
    }

    .custom-button__tag.active & {
      color: $background-white;
    }
  }

  &__dropdown {
    color: $color-text_main;
  }
}

.button-icon {
  height: 16px;
  width: 16px;
  object-fit: contain;
  display: block;

  &__dropdown {
    height: 10px;
    width: 10px;

    &:active,
    &.active {
      transform: scaleY(-1);
    }
  }
}

.custom-button:disabled,
.custom-button.disabled {
  background: #AEC9B4 !important;
  cursor: not-allowed !important;
  box-shadow: inset 0px -2px 0px #A2BCA8;
}

.custom-button:disabled:hover,
.custom-button.disabled:hover {
  background: #AEC9B4 !important;
  cursor: not-allowed !important;
  box-shadow: inset 0px -2px 0px #A2BCA8;
}
</style>